import { assign } from '@monofront/xstate-v4';
import { select } from 'radash';
import { ICustomer, ICustomerAddress } from '@monofront/types';
import {
  ACTIONS,
  Context,
  FinishFormEvent,
  InitFormDataEvent,
  MultiSelectStepContextData,
  MultiStepFormStep,
  MultiStepFormStepType,
  SaveFormDataEvent,
  TAGS,
} from './types';

const findMatchingOptions = (
  availableCollectionHandles: Array<string>,
  preferredCollections: Array<string> | null | undefined,
) => {
  if (!preferredCollections || !preferredCollections.length) return [];

  return select(
    availableCollectionHandles,
    (option) => option,
    (option) => preferredCollections.includes(option),
  );
};

export const generateActions = ({
  steps,
  onFormStarted,
  onFormStepSubmitted,
  onFormExited,
  onFormFinished,
  checkEligibility,
  addTag,
  removeTag,
}: {
  steps: Array<MultiStepFormStep>;
  onFormStarted: () => void;
  onFormStepSubmitted: (data: {
    stepNumber: number;
    stepName: string;
    answersSelected: string;
    displayedAnswers: Record<string, string>;
    collectionsToSave: Array<string>;
  }) => void;
  onFormExited: () => void;
  onFormFinished: () => void;
  customer: ICustomer | null;
  checkEligibility: (address: ICustomerAddress) => void;
  addTag: (tag: string) => void;
  removeTag: (tag: string) => void;
}) => {
  return {
    [ACTIONS.INIT_FORM_DATA]: assign<Context, InitFormDataEvent>(
      (context, event) => {
        const initialData = context.steps ?? {};

        addTag(TAGS.FORM_STARTED);

        steps.forEach((step) => {
          switch (step.type) {
            case 'MultiSelectCollection': {
              const availableCollectionsHandle = step.collections.map(
                ({ collection }) => collection.handle,
              );

              initialData[step.uid] = {
                stepId: step.uid,
                collections: findMatchingOptions(
                  availableCollectionsHandle,
                  event.data.savedCollections,
                ),
                address: {
                  street: '',
                  city: '',
                  postcode: '',
                  country: '',
                },
              };

              break;
            }
            case 'AddressForm': {
              const address = event.data.savedAddress as ICustomerAddress;

              initialData[step.uid] = {
                stepId: step.uid,
                collections: [],
                address: {
                  street: address.street,
                  city: address.city,
                  postcode: address.postalCode,
                  country: address.country,
                },
              };
              break;
            }
            default:
              break;
          }
        });

        return {
          ...context,
          steps: initialData,
          savedCollections: event.data.savedCollections,
        };
      },
    ),
    [ACTIONS.START_FORM]: assign<Context>((context, _event) => {
      onFormStarted();

      return context;
    }),
    [ACTIONS.SAVE_FORM_DATA]: assign<Context, SaveFormDataEvent>(
      (context, event) => {
        const step = steps.find(({ uid }) => uid === event.payload.stepId);

        if (!step) {
          throw new Error('Step not found while saving form data.');
        }

        const currentStepType = step.type;

        switch (currentStepType) {
          case MultiStepFormStepType.MultiSelectCollection: {
            const { collections } =
              (event.payload as MultiSelectStepContextData) || {};

            const multiSelectCollectionSteps = steps.filter(
              (step) =>
                step.type === MultiStepFormStepType.MultiSelectCollection,
            );

            const displayedAnswers = {};
            const selectedAnswers: number[] = [];

            for (let i = 0; i < step.collections.length; i++) {
              const collection = collections[i];

              if (collection) {
                const compensatedIndex = i + 1;

                displayedAnswers[`answers_displayed_${compensatedIndex}`] =
                  collection;
                if (collections.includes(collection)) {
                  selectedAnswers.push(compensatedIndex);
                }
              }
            }

            onFormStepSubmitted({
              stepNumber:
                multiSelectCollectionSteps.findIndex(
                  (step) => step.id === event.payload.stepId,
                ) + 1,
              stepName: step.uid,
              answersSelected: selectedAnswers.join(','),
              displayedAnswers,
              collectionsToSave: (event.payload as MultiSelectStepContextData)
                .collections,
            });

            context.savedCollections = (
              event.payload as MultiSelectStepContextData
            ).collections;

            break;
          }
          case MultiStepFormStepType.AddressForm: {
            const { street, city, postcode, country } =
              event.payload.address || {};

            if (street && city && postcode) {
              checkEligibility({
                street,
                city,
                postalCode: postcode,
                country: country || 'FR',
              });
            }
            break;
          }
          default:
            break;
        }

        return { ...context, ...event.payload };
      },
    ),
    [ACTIONS.EXIT_FORM]: assign((context, _event) => {
      onFormExited();

      return context;
    }),
    [ACTIONS.FINISH_FORM]: assign<Context, FinishFormEvent>(
      (context, event) => {
        if (event.payload.stepId === 'address-form') {
          const { street, city, postcode, country } =
            event.payload.address || {};

          if (street && city && postcode) {
            checkEligibility({
              street,
              city,
              postalCode: postcode,
              country: country || 'FR',
            });
          }
        }

        removeTag(TAGS.FORM_STARTED);
        addTag(TAGS.FORM_FINISHED);

        onFormFinished();

        return { ...context, ...event.payload };
      },
    ),
  };
};
